/**
 * Locate tour targets on the live page by what the user sees — a field's
 * label, a button's text, a placeholder — so tours need no hooks added to the
 * pages they explain.
 */

const clean = (s) => (s || '')
  .replace(/\*/g, '')
  .replace(/\(\d+\)/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

function visible(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function main() {
  return document.querySelector('main.content-area') || document.body;
}

/** The wrapper of the field whose label starts with `text`. */
export function findFieldByLabel(text, root = main()) {
  const want = clean(text);
  const labels = Array.from(root.querySelectorAll('label, .tk-label')).filter(visible);
  // Exact text first, so "Description" never lands on a "Description of issue" field.
  const label = labels.find((l) => clean(l.textContent) === want)
    || labels.find((l) => clean(l.textContent).startsWith(want));
  return label ? label.parentElement : null;
}

export function findButton(text, root = main()) {
  const want = clean(text);
  const buttons = Array.from(root.querySelectorAll('button, a'));
  return buttons.find((b) => visible(b) && clean(b.textContent).includes(want)) || null;
}

export function findByPlaceholder(text, root = main()) {
  const input = Array.from(root.querySelectorAll('input, textarea'))
    .find((el) => visible(el) && (el.getAttribute('placeholder') || '').startsWith(text));
  return input || null;
}

export function findHeading(text, root = main()) {
  const want = clean(text);
  return Array.from(root.querySelectorAll('h1, h2, h3'))
    .find((h) => visible(h) && clean(h.textContent).startsWith(want)) || null;
}

export function findSelectWithOption(optionText, root = main()) {
  return Array.from(root.querySelectorAll('select'))
    .find((s) => visible(s) && Array.from(s.options).some((o) => o.textContent.trim() === optionText)) || null;
}

/** Resolve a step's `target` spec to an element (or null). */
export function resolveTarget(target) {
  if (!target) return null;
  if (typeof target === 'function') return target();
  if (target.label) return findFieldByLabel(target.label);
  if (target.button) return findButton(target.button);
  if (target.placeholder) {
    const el = findByPlaceholder(target.placeholder);
    return el && target.wrap ? el.parentElement : el;
  }
  if (target.heading) return findHeading(target.heading);
  if (target.selectWithOption) return findSelectWithOption(target.selectWithOption);
  if (target.selector) {
    const scope = target.global ? document : main();
    return Array.from(scope.querySelectorAll(target.selector)).find(visible) || null;
  }
  return null;
}

export function describeControl(wrapper) {
  if (wrapper.querySelector('.tk-ss, select')) return 'Open the dropdown and choose one option. You can type to filter the list.';
  if (wrapper.querySelector('textarea')) return 'Type a full explanation in this box.';
  if (wrapper.querySelector('input[type="date"]')) return 'Pick a date from the calendar.';
  if (wrapper.querySelector('input[type="number"]')) return 'Enter a number.';
  if (wrapper.querySelector('input[type="email"]')) return 'Enter an email address.';
  if (wrapper.querySelector('input[type="checkbox"]')) return 'Tick every option that applies.';
  if (wrapper.querySelector('input[type="radio"]')) return 'Select one of the options.';
  if (wrapper.querySelector('input[type="file"]')) return 'Choose a file from your computer.';
  return 'Type the value in this box.';
}

/**
 * Steps for every field rendered between two labelled points of a form —
 * used for product-specific ticket fields, which differ per product.
 */
export function expandFieldsBetween({ afterHeading, untilLabel, skipLabels = [] }) {
  const heading = findHeading(afterHeading);
  const until = findFieldByLabel(untilLabel);
  if (!heading || !until) return [];
  const skip = skipLabels.map(clean);
  const steps = [];
  let node = heading.closest('.tk-span-2') || heading;
  node = node.nextElementSibling;
  while (node && node !== until) {
    const label = node.querySelector(':scope > label, :scope > .tk-label');
    if (label && visible(node)) {
      const name = label.textContent.replace(/\*/g, '').trim();
      if (!skip.includes(clean(name))) {
        const required = label.textContent.includes('*');
        const help = Array.from(node.children)
          .filter((c) => c !== label && !c.matches('input, select, textarea, .tk-ss') && !c.querySelector('input, select, textarea'))
          .map((c) => c.textContent.trim())
          .filter(Boolean)
          .join(' ');
        const element = node;
        steps.push({
          target: () => element,
          title: `${name}${required ? ' (required)' : ' (optional)'}`,
          body: [help, describeControl(node)].filter(Boolean).join(' '),
        });
      }
    }
    node = node.nextElementSibling;
  }
  return steps;
}
